import { AppShell } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { useEffect } from 'react';
import { useLocation } from '@tanstack/react-router';
import { AppHeader } from './AppHeader';
import { AppNavbar } from './AppNavbar.tsx';
import { FeedbackFloatingIcon } from '@/features/incidentIntegration/components/FeedbackFloatingIcon';
import { ConnectionStatusIndicator } from '@/signalr/components/ConnectionStatusIndicator.tsx';
import { themeConfig } from "@/ui/theme/theme.config.ts";

interface BaseWebAppLayoutProps {
  children: React.ReactNode;
}

export const BaseWebAppLayout = ({
                                   children,
                                 }: BaseWebAppLayoutProps) => {
  const [mobileOpened, {toggle: toggleMobile, close: closeMobile}] = useDisclosure();
  const [desktopOpened, {toggle: toggleDesktop}] = useDisclosure(true);
  const isDesktop = useMediaQuery(`(min-width: ${themeConfig.breakpoints?.md})`);
  const location = useLocation();

  // Auto-close mobile nav when resizing to desktop
  useEffect(() => {
    if (isDesktop && mobileOpened) {
      closeMobile();
    }
  }, [isDesktop, mobileOpened, closeMobile]);

  // Close mobile nav on route change
  useEffect(() => {
    if (mobileOpened) {
      closeMobile();
    }
    // Only react to location changes, not mobileOpened
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <>
      <AppShell
        // padding={{ base: 'md', md: 'md', lg: undefined }}
        pl={{base: '1.25rem', lg: undefined}}
        pr={{base: '1.375rem', lg: undefined}}
        header={{
          height: 60
        }}
        navbar={{
          width: desktopOpened ? 260 : 55,
          breakpoint: 'md',
          collapsed: {mobile: !mobileOpened},
        }}
        withBorder={false}
      >
        <AppShell.Header>
          <AppHeader mobileOpened={mobileOpened} toggleMobile={toggleMobile}/>
        </AppShell.Header>

        <AppShell.Navbar aria-label="main-navigation">
          <AppNavbar desktopOpened={desktopOpened} toggleDesktop={toggleDesktop} mobileOpened={mobileOpened} closeMobile={closeMobile}/>
        </AppShell.Navbar>

        <AppShell.Main pb="xl" pt="var(--app-shell-header-height, 0)">
          <ConnectionStatusIndicator/>
          {children}
        </AppShell.Main>
      </AppShell>

      {/* Feedback Floating Icon */}
      <FeedbackFloatingIcon/>
    </>
  );
};
